import { Alert, FormGroup, FormLabel, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createCollaborator, CreateCollaboratorPayload, getCollaboratorsByCompanyAndId, updateCollaborator } from "src/apis/company";
import CompanySelect from "src/pages/components/CompanySelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { getUserCompanyId, isUserAdministrator, isUserCompany } from "src/utils/auth";
import { isSuccessOrAllFieldsAreFilled } from "src/utils/form";
import { Id } from "src/utils/types";
import { useCompanyBlocks } from "src/context/CompanyBlocksContext";

const initialValues: CreateCollaboratorPayload = {
  cargo: '',
  nome: '',
  observacoes: '',
  setor: '',
  unidade: '',
}

const RegisterCollaborator: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<Id>(0);
  const [formValues, setFormValues] = useState<CreateCollaboratorPayload>(initialValues);
  const {id} = useParams();

  const isUpdate = useMemo<boolean>(() => Boolean(id), [id]);
  const isSubmitDisabled = useMemo<boolean>(() => isSuccessOrAllFieldsAreFilled(success, formValues), [success, formValues]);
  const isCompany = useMemo<boolean>(() => isUserCompany() && !isUserAdministrator(), []);
  const { isActionRestricted } = useCompanyBlocks();
  const isInviteBlocked = useMemo(() => isCompany && isActionRestricted("invite_collaborator"), [isActionRestricted, isCompany]);

  useEffect(() => {
    if (isCompany) {
      setCompanyId(getUserCompanyId());
    }
  }, [isCompany]);
  
  const resetFormState = (): void => {
    setError('');
    setSuccess(false);
    setFormValues(initialValues);
  }

  const fetchCollaborator = useCallback(async (collaboratorId: Id): Promise<void> => {
    console.log(`[fetchCollaborator] Iniciando busca para colaborador ID: ${collaboratorId}`);

    if (!collaboratorId) {
        console.error("[fetchCollaborator] ERRO: O ID do colaborador é obrigatório.");
        setError("O ID do colaborador é obrigatório.");
        return;
    }

    if (!companyId) {
        console.error("[fetchCollaborator] ERRO: O ID da empresa não está definido.");
        setError("O ID da empresa não está definido.");
        return;
    }

    try {
        console.log(`[fetchCollaborator] Buscando dados do colaborador ${collaboratorId} na empresa ${companyId}...`);
        const res = await getCollaboratorsByCompanyAndId(companyId, collaboratorId);
        
        if (!res.id_empresa) {
            console.error("[fetchCollaborator] ERRO: O ID da empresa não foi retornado pela API.");
            throw new Error("O ID da empresa não foi retornado pela API.");
        }

        console.log("[fetchCollaborator] Dados do colaborador recebidos com sucesso:", res);

        setCompanyId(res.id_empresa);
        setFormValues({
            cargo: res.cargo,
            nome: res.nome,
            observacoes: res.observacoes,
            setor: res.setor,
            unidade: res.unidade,
        });
        setError('');
    } catch (err: any) {
        console.error("[fetchCollaborator] ERRO ao buscar colaborador:", err?.message || err);
        setError(err?.message || "Erro ao buscar colaborador.");
        setSuccess(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (id && companyId) {
      fetchCollaborator(id);
    }
  }, [id, companyId, fetchCollaborator]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isUpdate) {
        await updateCollaborator(companyId, id, formValues);
      } else {
        await createCollaborator(companyId, formValues);
      }

      setError('');
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <PageContainer title={isUpdate ? "Editando Colaborador" : "Adicionar Colaborador"} error={error}>
      {isInviteBlocked ? (
        <Alert severity="warning">
          O cadastro de colaboradores está temporariamente bloqueado para esta empresa. Entre em contato com a EezyCare para regularizar a situação.
        </Alert>
      ) : (
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {!isCompany && (
          <CompanySelect
            companyId={companyId}
            setCompanyId={setCompanyId}
            setError={setError}
          />
        )}

        {Boolean(companyId) && (
          <>
            <FormGroup>
              <FormLabel>Nome completo</FormLabel>
              <TextField
                type="text"
                value={formValues.nome}
                onChange={(e) => {
                  setFormValues(previousValues => ({
                    ...previousValues,
                    nome: e.target.value as string,
                  }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Cargo</FormLabel>
              <TextField
                type="text"
                value={formValues.cargo}
                onChange={(e) => {
                  setFormValues(previousValues => ({
                    ...previousValues,
                    cargo: e.target.value as string,
                  }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Setor</FormLabel>
              <TextField
                type="text"
                value={formValues.setor}
                onChange={(e) => {
                  setFormValues(previousValues => ({
                    ...previousValues,
                    setor: e.target.value as string,
                  }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Unidade</FormLabel>
              <TextField
                type="text"
                value={formValues.unidade}
                onChange={(e) => {
                  setFormValues(previousValues => ({
                    ...previousValues,
                    unidade: e.target.value as string,
                  }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Observações</FormLabel>
              <TextField
                multiline
                type="text"
                value={formValues.observacoes}
                onChange={(e) => {
                  setFormValues(previousValues => ({
                    ...previousValues,
                    observacoes: e.target.value as string,
                  }));
                }}
              />
            </FormGroup>

            <FormSaveAndResetButtons
              success={success}
              loading={loading}
              isSubmitDisabled={isSubmitDisabled}
              resetFormState={resetFormState}
              isResetFormStateDisabled={isUpdate}
            />
          </>
        )}
      </form>
      )}
    </PageContainer>
  )
}

export default RegisterCollaborator;
